/**
 * Admin → Contracts → Contract Packs Sent
 *
 * Cross-customer table of every contract pack with filters, status pills,
 * and per-row actions (view / download / counter-sign / resend / archive).
 *
 * Backend: GET /api/contract-packs/admin/list
 *          POST /api/customers/{cid}/contract-pack/{pid}/archive (+unarchive)
 *          POST /api/customers/{cid}/contract-pack/{pid}/send (resend)
 *          POST /api/customers/{cid}/contract-pack/{pid}/admin-sign
 *          GET  /api/customers/{cid}/contract-pack/{pid}/preview.pdf
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  FileSignature, FilePen, Eye, Download, Loader2, CheckCircle2, Send, Archive,
  ArchiveRestore, Search, Filter, RotateCcw, ExternalLink, Copy, ShieldCheck, Ban,
  Cloud, Database, Link2, RefreshCw, AlertTriangle, BellRing,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { SignaturePad } from '../components/SignaturePad';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Awaiting customer' },
  { value: 'customer_signed', label: 'Customer signed' },
  { value: 'fully_signed', label: 'Complete' },
  { value: 'voided', label: 'Voided' },
];

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Awaiting Customer', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  customer_signed: { label: 'Customer Signed — Needs You', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  fully_signed: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  voided: { label: 'Voided', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const PACK_LABEL = {
  solar_battery: 'Solar + Battery',
  solar_only: 'Solar Only',
  battery_only: 'Battery Only',
};

const DAYS_RAISED_OPTIONS = [
  { value: '0', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const AWAITING_OPTIONS = [
  { value: '0', label: 'Any duration' },
  { value: '3', label: '≥ 3 days' },
  { value: '7', label: '≥ 7 days' },
  { value: '14', label: '≥ 14 days' },
  { value: '21', label: '≥ 21 days' },
];

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch { return ''; }
}

// Reusable backup-status cell for any customer document (LoA / Variation).
// synced → green tick; has-local-but-not-synced → amber "Sync" pill;
// no source → grey dash.
const DocBackupCell = ({ synced, hasLocal, webUrl, syncing, onSync, testid }) => {
  if (synced) {
    return (
      <a
        href={webUrl || undefined}
        target={webUrl ? '_blank' : undefined}
        rel="noreferrer"
        className="inline-flex justify-center text-emerald-600 hover:text-emerald-700"
        title={webUrl ? 'Backed up to SharePoint — click to open' : 'Backed up to SharePoint'}
        data-testid={`backup-ok-${testid}`}
        onClick={(e) => { if (!webUrl) e.preventDefault(); }}
      >
        <CheckCircle2 className="w-4 h-4" />
      </a>
    );
  }
  if (hasLocal) {
    return (
      <button
        type="button"
        onClick={onSync}
        disabled={syncing}
        title="On disk but not yet on SharePoint. Click to sync now."
        className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-60"
        data-testid={`backup-partial-${testid}`}
      >
        {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{syncing ? 'Syncing…' : 'Sync'}</span>
      </button>
    );
  }
  return (
    <span className="text-slate-300 dark:text-slate-600 select-none" title="No durable copy yet" data-testid={`backup-none-${testid}`}>—</span>
  );
};

async function copyLink(url) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Signing link copied to clipboard');
  } catch {
    window.prompt('Copy this signing link:', url);
  }
}

// Reusable "Link actions" dropdown for any signable document (Contract Pack /
// Variation / LoA). Offers: copy current link, generate a fresh link, re-send.
const LinkActionsMenu = ({ currentLink, onGenerate, onResend, busy, testid }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="ghost" className="h-8" title="Signing link options" data-testid={`link-actions-${testid}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <DropdownMenuLabel className="text-xs">Signing link</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {currentLink && (
        <DropdownMenuItem onClick={() => copyLink(currentLink)} data-testid={`copy-current-${testid}`}>
          <Copy className="w-3.5 h-3.5 mr-2" /> Copy current link
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={onGenerate} data-testid={`gen-link-${testid}`}>
        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Generate new link
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onResend} data-testid={`resend-link-${testid}`}>
        <Send className="w-3.5 h-3.5 mr-2" /> Re-send to customer
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const AdminContractPacks = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [raisedWithin, setRaisedWithin] = useState('0');
  const [awaitingDaysMin, setAwaitingDaysMin] = useState('0');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [counterSign, setCounterSign] = useState(null); // { customer_id, pack_id, customer_name }
  const [signature, setSignature] = useState('');
  const [signing, setSigning] = useState(false);
  const [resendingNotify, setResendingNotify] = useState(null); // pack_id re-alerting admins
  // Bulk recovery state — used by the "Recover signed packs" button to
  // re-render + re-persist every signed pack to GridFS + SharePoint.
  const [recovering, setRecovering] = useState(false);
  const [recoveryReport, setRecoveryReport] = useState(null);
  const [syncingId, setSyncingId] = useState(null); // pack_id currently syncing to SharePoint
  const [syncingDocId, setSyncingDocId] = useState(null); // doc_id (LoA/variation) syncing
  const [linkBusyId, setLinkBusyId] = useState(null); // pack_id/doc_id with a link action in flight
  const [packBackupFilter, setPackBackupFilter] = useState('all'); // 'all' | 'attention'
  const [loas, setLoas] = useState([]);
  const [loasLoading, setLoasLoading] = useState(false);
  const [remindingLoa, setRemindingLoa] = useState(null);
  const [loaSearch, setLoaSearch] = useState('');
  // Contract Variations cross-customer list (parallel to LoAs above)
  const [variations, setVariations] = useState([]);
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [remindingVariation, setRemindingVariation] = useState(null);
  const [variationSearch, setVariationSearch] = useState('');
  const [syncStatus, setSyncStatus] = useState(null); // last SharePoint sweep summary

  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get('/admin/documents/sharepoint-sync-status');
      setSyncStatus(data || null);
    } catch {
      setSyncStatus(null);
    }
  };

  useEffect(() => { loadSyncStatus(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (search.trim()) params.search = search.trim();
      if (raisedWithin !== '0') params.raised_within_days = raisedWithin;
      if (awaitingDaysMin !== '0') params.awaiting_days_min = awaitingDaysMin;
      if (includeArchived) params.include_archived = true;
      const { data } = await api.get('/contract-packs/admin/list', { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contract packs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Live re-query whenever filters change
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [status, search, raisedWithin, awaitingDaysMin, includeArchived]);

  const loadLoas = async () => {
    setLoasLoading(true);
    try {
      const { data } = await api.get('/loa/admin/list');
      setLoas(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Letters of Authority');
      setLoas([]);
    } finally {
      setLoasLoading(false);
    }
  };

  useEffect(() => { loadLoas(); /* eslint-disable-next-line */ }, []);

  const loadVariations = async () => {
    setVariationsLoading(true);
    try {
      const { data } = await api.get('/variations/admin/list');
      setVariations(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Contract Variations');
      setVariations([]);
    } finally {
      setVariationsLoading(false);
    }
  };

  useEffect(() => { loadVariations(); /* eslint-disable-next-line */ }, []);

  const sendVariationReminder = async (v) => {
    if (!window.confirm(
      `Send a sign reminder to ${v.customer_name} for their Contract Variation?\n\n` +
      `A fresh 24-hour signing link will be generated and emailed to ${v.customer_email}.`
    )) return;
    setRemindingVariation(v.doc_id);
    try {
      const r = await api.post(`/customers/${v.customer_id}/sign-doc/${v.doc_id}/send-reminder`);
      toast.success(`Reminder sent (#${r.data.reminder_count})`);
      loadVariations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingVariation(null);
    }
  };

  // Client-side Variation search — same shape as LoA search
  const filteredVariations = variations.filter((v) => {
    if (!variationSearch.trim()) return true;
    const q = variationSearch.toLowerCase();
    return (
      (v.customer_name || '').toLowerCase().includes(q) ||
      (v.customer_email || '').toLowerCase().includes(q) ||
      (v.status || '').toLowerCase().includes(q) ||
      (v.filename || '').toLowerCase().includes(q) ||
      String(v.adjusted_total || '').includes(q)
    );
  });

  const sendLoaReminder = async (loa) => {
    if (!window.confirm(
      `Send a sign reminder to ${loa.customer_name} for their Letter of Authority?\n\n` +
      `A fresh 24-hour signing link will be generated and emailed to ${loa.customer_email}.`
    )) return;
    setRemindingLoa(loa.doc_id);
    try {
      const r = await api.post(`/customers/${loa.customer_id}/sign-doc/${loa.doc_id}/send-reminder`);
      toast.success(`Reminder sent (#${r.data.reminder_count})`);
      loadLoas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingLoa(null);
    }
  };

  // Client-side LoA search
  const filteredLoas = loas.filter((l) => {
    if (!loaSearch.trim()) return true;
    const q = loaSearch.toLowerCase();
    return (
      (l.customer_name || '').toLowerCase().includes(q) ||
      (l.customer_email || '').toLowerCase().includes(q) ||
      (l.dno_name || '').toLowerCase().includes(q) ||
      (l.application_type || '').toLowerCase().includes(q) ||
      (l.status || '').toLowerCase().includes(q)
    );
  });

  const counts = useMemo(() => {
    const c = { total: rows.length, draft: 0, sent: 0, customer_signed: 0, fully_signed: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [rows]);

  // Backup health across the currently-loaded packs. "Missing" only counts
  // signed packs (which *should* be durably backed up) that have no snapshot.
  const backupHealth = useMemo(() => {
    const h = { backedUp: 0, partial: 0, missing: 0, relevant: 0 };
    rows.forEach((r) => {
      const signed = r.status === 'customer_signed' || r.status === 'fully_signed';
      const hasGridfs = !!r.frozen_pdf_gridfs_id;
      const hasSp = !!r.frozen_pdf_sharepoint_item_id;
      if (hasGridfs && hasSp) { h.backedUp += 1; h.relevant += 1; }
      else if (hasGridfs) { h.partial += 1; h.relevant += 1; }
      else if (signed) { h.missing += 1; h.relevant += 1; }
    });
    return h;
  }, [rows]);

  // Rows shown in the Contract Packs table — optionally narrowed to just the
  // packs that need a backup (partial or missing) when the chip filter is on.
  const displayedRows = useMemo(() => {
    if (packBackupFilter !== 'attention') return rows;
    return rows.filter((r) => {
      const signed = r.status === 'customer_signed' || r.status === 'fully_signed';
      const hasGridfs = !!r.frozen_pdf_gridfs_id;
      const hasSp = !!r.frozen_pdf_sharepoint_item_id;
      const partial = hasGridfs && !hasSp;
      const missing = signed && !hasGridfs;
      return partial || missing;
    });
  }, [rows, packBackupFilter]);

  const resetFilters = () => {
    setStatus('all');
    setSearch('');
    setRaisedWithin('0');
    setAwaitingDaysMin('0');
    setIncludeArchived(false);
    setPackBackupFilter('all');
  };

  const view = async (r) => {
    try {
      const resp = await api.get(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/preview.pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const download = async (r) => {
    try {
      const resp = await api.get(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/preview.pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ContractPack-${(r.customer_name || 'customer').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const resend = async (r) => {
    try {
      await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/send`, { send_email: true });
      toast.success('Signing link re-sent to customer');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    }
  };

  // Generate a FRESH contract-pack signing link without emailing, then copy it.
  const generatePackLink = async (r) => {
    setLinkBusyId(r.pack_id);
    try {
      const { data } = await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/send`, { send_email: false });
      if (data.sign_link) await copyLink(data.sign_link);
      else toast.success('New signing link generated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate link');
    } finally {
      setLinkBusyId(null);
    }
  };

  // Generate a FRESH LoA/Variation signing link without emailing, then copy it.
  const generateDocLink = async (customerId, docId, reload) => {
    setLinkBusyId(docId);
    try {
      const { data } = await api.post(`/customers/${customerId}/sign-doc/${docId}/share-link`);
      if (data.public_url) await copyLink(data.public_url);
      else toast.success('New signing link generated');
      if (reload) reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate link');
    } finally {
      setLinkBusyId(null);
    }
  };

  // Re-send a LoA/Variation signing link uses the existing reminder handlers
  // (sendLoaReminder / sendVariationReminder) which confirm + email + reload.

  const archive = async (r) => {
    if (!window.confirm('Archive this contract pack? It will be hidden from the default view.')) return;
    try {
      await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/archive`);
      toast.success('Pack archived');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const unarchive = async (r) => {
    try {
      await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/unarchive`);
      toast.success('Pack restored');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unarchive failed');
    }
  };

  const voidPack = async () => {
    const reason = voidReason.trim();
    if (!reason) { toast.error('Please enter a reason for voiding'); return; }
    setVoiding(true);
    try {
      await api.post(`/customers/${voidTarget.customer_id}/contract-pack/${voidTarget.pack_id}/void`, { reason });
      toast.success('Contract voided — a fresh one can now be raised for this customer');
      setVoidTarget(null);
      setVoidReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally {
      setVoiding(false);
    }
  };

  const resendCounterSignNotify = async (r) => {
    setResendingNotify(r.pack_id);
    try {
      const { data } = await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/resend-countersign-notify`);
      toast.success(`Counter-sign alert re-sent to ${data.admin_count || 0} admin${data.admin_count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not re-send counter-sign alert');
    } finally {
      setResendingNotify(null);
    }
  };

  const submitCounterSign = async () => {
    if (!signature) { toast.error('Please sign in the box above first'); return; }
    setSigning(true);
    try {
      await api.post(`/customers/${counterSign.customer_id}/contract-pack/${counterSign.pack_id}/admin-sign`, {
        signature_data_url: signature,
      });
      toast.success('Contract fully executed — copy emailed to customer');
      setCounterSign(null);
      setSignature('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Counter-sign failed');
    } finally {
      setSigning(false);
    }
  };

  // One-click bulk recovery. Runs the backend backfill which re-renders every
  // signed pack lacking a frozen GridFS snapshot, persists the bytes durably
  // and pushes a copy to SharePoint. Safe to run multiple times — packs that
  // already have a snapshot are skipped (only_missing=true).
  const handleRecoverSigned = async () => {
    if (!confirm('Re-render every signed contract pack that doesn\'t have a backup snapshot? This persists each to GridFS and syncs to SharePoint.')) return;
    setRecovering(true);
    setRecoveryReport(null);
    const t = toast.loading('Recovering signed packs… this may take a minute.');
    try {
      const { data } = await api.post('/contract-packs/admin/freeze-all?only_missing=true');
      setRecoveryReport(data);
      const sp = (data.details || []).filter((d) => d?.sharepoint === 'uploaded').length;
      toast.success(
        `Processed ${data.processed} pack${data.processed === 1 ? '' : 's'} — frozen ${data.frozen}, SharePoint ${sp}, failed ${data.failed}`,
        { id: t, duration: 7000 },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed', { id: t });
    } finally {
      setRecovering(false);
    }
  };

  // One-click "Sync to SharePoint" for a single partially-backed-up pack.
  // The pack already has a frozen GridFS snapshot but no SharePoint copy — this
  // pushes the existing bytes (no re-render) so the amber icon turns green.
  const handleSyncSharePoint = async (r) => {
    setSyncingId(r.pack_id);
    const t = toast.loading('Syncing to SharePoint…');
    try {
      const { data } = await api.post(`/customers/${r.customer_id}/contract-pack/${r.pack_id}/sync-sharepoint`);
      if (data.status === 'already_synced') {
        toast.success('Already backed up to SharePoint', { id: t });
      } else {
        toast.success('Backed up to SharePoint', { id: t });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SharePoint sync failed', { id: t });
    } finally {
      setSyncingId(null);
    }
  };

  // Generic "Sync to SharePoint" for any customer document (LoA / Variation /
  // uploaded file). Uses the shared backup endpoint, then reloads the lists.
  const handleSyncDoc = async (customerId, docId, reload) => {
    setSyncingDocId(docId);
    const t = toast.loading('Syncing to SharePoint…');
    try {
      const { data } = await api.post(`/customers/${customerId}/documents/${docId}/sharepoint-backup`);
      toast.success(data.already_synced ? 'Already backed up to SharePoint' : 'Backed up to SharePoint', { id: t });
      if (reload) reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SharePoint sync failed', { id: t });
    } finally {
      setSyncingDocId(null);
    }
  };

  // Run the full SharePoint sweep on demand (same job as the nightly midnight
  // task). Refreshes the "last sync" banner + all three lists afterwards.
  const [syncingAll, setSyncingAll] = useState(false);
  const handleSyncAll = async () => {
    setSyncingAll(true);
    const t = toast.loading('Running SharePoint backup sweep…');
    try {
      const { data } = await api.post('/admin/documents/sync-all-sharepoint');
      const synced = (data.documents_synced || 0) + (data.packs_synced || 0);
      const failed = (data.documents_failed || 0) + (data.packs_failed || 0);
      toast.success(`SharePoint sweep done — ${synced} backed up${failed ? ` · ${failed} failed` : ''}`, { id: t, duration: 7000 });
      loadSyncStatus();
      load();
      loadLoas();
      loadVariations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SharePoint sweep failed', { id: t });
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="admin-contract-packs-page">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-violet-600" />
              Contract Packs Sent
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Every contract pack across all customers. Filter by date raised, status duration, or customer details.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className="bg-slate-50">Total: {counts.total}</Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Awaiting: {counts.sent}</Badge>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Customer signed: {counts.customer_signed}</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Complete: {counts.fully_signed}</Badge>
            {backupHealth.relevant > 0 && (
              <div
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1 text-xs"
                title="Backup health across signed contract packs (GridFS + SharePoint)"
                data-testid="backup-health-chip"
              >
                <span className="font-medium text-slate-500 dark:text-slate-400">Backups</span>
                <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="backup-health-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" />{backupHealth.backedUp}
                </span>
                <button
                  type="button"
                  onClick={() => setPackBackupFilter((f) => (f === 'attention' ? 'all' : 'attention'))}
                  title={packBackupFilter === 'attention' ? 'Showing only packs needing backup — click to clear' : 'Show only packs needing backup (partial / missing)'}
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-amber-600 transition-colors ${packBackupFilter === 'attention' ? 'bg-amber-100 dark:bg-amber-900/40 ring-1 ring-amber-300' : 'hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
                  data-testid="backup-health-partial"
                >
                  <Cloud className="w-3.5 h-3.5" />{backupHealth.partial} partial
                </button>
                {backupHealth.missing > 0 && (
                  <button
                    type="button"
                    onClick={() => setPackBackupFilter((f) => (f === 'attention' ? 'all' : 'attention'))}
                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-rose-600 transition-colors ${packBackupFilter === 'attention' ? 'bg-rose-100 dark:bg-rose-900/40 ring-1 ring-rose-300' : 'hover:bg-rose-50 dark:hover:bg-rose-900/20'}`}
                    data-testid="backup-health-missing"
                  >
                    <Database className="w-3.5 h-3.5" />{backupHealth.missing} missing
                  </button>
                )}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecoverSigned}
              disabled={recovering}
              className="ml-2"
              data-testid="recover-signed-btn"
              title="Re-render every signed pack and back it up to GridFS + SharePoint. Safe to run multiple times."
            >
              {recovering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-600" />}
              {recovering ? 'Recovering…' : 'Recover signed packs'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncAll}
              disabled={syncingAll}
              data-testid="sync-all-sharepoint-btn"
              title="Push every unsynced document and signed contract pack to SharePoint now (same job as the nightly midnight sync)."
            >
              {syncingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Cloud className="w-3.5 h-3.5 mr-1 text-sky-600" />}
              {syncingAll ? 'Syncing…' : 'Sync all to SharePoint'}
            </Button>
          </div>
        </div>

        {/* Inline recovery report — auto-dismisses on next page action */}
        {recoveryReport && (
          <div
            className="mt-2 mb-2 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800 px-3 py-2 flex items-start gap-2"
            data-testid="recovery-report"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-xs text-emerald-900 dark:text-emerald-200">
              <p className="font-semibold">
                Recovery complete — {recoveryReport.frozen}/{recoveryReport.processed} pack{recoveryReport.processed === 1 ? '' : 's'} frozen
                {recoveryReport.failed > 0 && <span className="text-rose-700"> · {recoveryReport.failed} failed</span>}
              </p>
              <p className="opacity-80 mt-0.5">
                SharePoint uploads: <strong>{(recoveryReport.details || []).filter((d) => d?.sharepoint === 'uploaded').length}</strong>
                {' · '}skipped (no SP folder): <strong>{(recoveryReport.details || []).filter((d) => d?.sharepoint === 'skipped').length}</strong>
                {' · '}SP errors: <strong>{(recoveryReport.details || []).filter((d) => String(d?.sharepoint || '').startsWith('failed')).length}</strong>
              </p>
            </div>
            <button
              onClick={() => setRecoveryReport(null)}
              className="text-emerald-700 hover:text-emerald-900 text-xs ml-2"
              data-testid="dismiss-recovery-report"
            >×</button>
          </div>
        )}

        {/* Last nightly/manual SharePoint sync result */}
        {syncStatus && (
          <div
            className={`mt-2 mb-2 rounded-lg border px-3 py-2 flex items-center gap-2 text-xs ${
              syncStatus.total_failed > 0
                ? 'border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-900 dark:text-amber-200'
                : 'border-sky-200 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 text-sky-900 dark:text-sky-200'
            }`}
            data-testid="sharepoint-sync-banner"
          >
            <Cloud className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-semibold">Last SharePoint sync:</span>{' '}
              <strong>{syncStatus.total_synced}</strong> backed up
              {syncStatus.total_failed > 0 && <span className="text-rose-700 dark:text-rose-400"> · {syncStatus.total_failed} failed</span>}
              {syncStatus.documents_missing > 0 && <span> · {syncStatus.documents_missing} missing locally</span>}
              <span className="opacity-70">
                {' · '}{fmtRelative(syncStatus.finished_at)} ({syncStatus.triggered_by === 'manual' ? 'manual' : 'nightly'})
              </span>
            </div>
          </div>
        )}


        {/* Filters */}
        <Card className="mb-4" data-testid="contract-packs-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500 font-medium block mb-1">Search name, address or email</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. Smith or 12 Acacia Avenue"
                  className="pl-9 h-9"
                  data-testid="contract-packs-search"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Raised within</label>
              <Select value={raisedWithin} onValueChange={setRaisedWithin}>
                <SelectTrigger className="h-9" data-testid="filter-raised-within"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS_RAISED_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Awaiting signature</label>
              <Select value={awaitingDaysMin} onValueChange={setAwaitingDaysMin}>
                <SelectTrigger className="h-9" data-testid="filter-awaiting"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AWAITING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-5 flex items-center justify-between flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="filter-include-archived"
                />
                Show archived packs
              </label>
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card data-testid="contract-packs-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-10 flex items-center justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading contract packs…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">
                No contract packs match these filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium">Customer</th>
                      <th className="text-left px-4 py-2.5 font-medium">Pack</th>
                      <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      <th className="text-left px-4 py-2.5 font-medium">Viewed</th>
                      <th className="text-left px-4 py-2.5 font-medium">Raised</th>
                      <th className="text-left px-4 py-2.5 font-medium">In status</th>
                      <th className="text-center px-4 py-2.5 font-medium">Backup</th>
                      <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {displayedRows.map((r) => {
                      const meta = STATUS_META[r.status] || STATUS_META.draft;
                      const stale = r.status === 'sent' && (r.days_in_status ?? 0) >= 7;
                      return (
                        <tr key={r.pack_id} className={`hover:bg-slate-50/70 dark:hover:bg-slate-800/30 ${r.archived ? 'opacity-60' : ''}`} data-testid={`row-${r.pack_id}`}>
                          <td className="px-4 py-3">
                            <Link
                              to={`/customers/${r.customer_id}`}
                              className="font-medium text-slate-800 dark:text-slate-100 hover:text-blue-600 inline-flex items-center gap-1"
                              data-testid={`customer-link-${r.pack_id}`}
                            >
                              {r.customer_name || '—'}
                              <ExternalLink className="w-3 h-3 opacity-60" />
                            </Link>
                            <div className="text-xs text-slate-500 truncate max-w-[280px]">{r.customer_address || ''}</div>
                            {r.archived && <Badge variant="outline" className="mt-1 bg-slate-100 text-slate-600 text-[10px]">Archived</Badge>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{PACK_LABEL[r.pack_type] || r.pack_type}</td>
                          <td className="px-4 py-3">
                            <Badge className={`${meta.cls} border text-[11px]`}>{meta.label}</Badge>
                            {(r.status === 'draft' || r.status === 'sent') && !r.customer_has_cutout && (
                              <Link
                                to={`/customers/${r.customer_id}`}
                                className="mt-1 inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 dark:bg-rose-900/20 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300 hover:bg-rose-100 transition-colors"
                                title="This contract can't be sent until a cut-out image is added to the customer's documents"
                                data-testid={`cutout-needed-${r.pack_id}`}
                              >
                                <AlertTriangle className="w-3 h-3" /> Cut-out needed
                              </Link>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {r.customer_first_viewed_at ? (
                              <div className="flex items-center gap-1.5" data-testid={`viewed-${r.pack_id}`}>
                                <Eye className="w-3.5 h-3.5 text-sky-600" />
                                <div className="text-xs">
                                  <div className="text-slate-700 dark:text-slate-200 font-medium">{fmt(r.customer_last_viewed_at || r.customer_first_viewed_at)}</div>
                                  {(r.customer_view_count || 0) > 1 && (
                                    <div className="text-slate-400">{r.customer_view_count}× opened</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400" data-testid={`not-viewed-${r.pack_id}`}>Not opened</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            <div>{fmt(r.created_at)}</div>
                            {r.days_raised != null && <div className="text-xs text-slate-400">{r.days_raised}d ago</div>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            <span className={stale ? 'text-amber-700 font-medium' : ''}>
                              {r.days_in_status != null ? `${r.days_in_status}d` : '—'}
                            </span>
                            {r.status === 'sent' && r.email_count > 0 && (
                              <div className="text-xs text-slate-400">Sent {r.email_count}×</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const hasGridfs = !!r.frozen_pdf_gridfs_id;
                              const hasSharepoint = !!r.frozen_pdf_sharepoint_item_id;
                              if (hasGridfs && hasSharepoint) {
                                return (
                                  <div className="flex justify-center" title={`Backed up to GridFS + SharePoint${r.frozen_at ? ` · ${fmt(r.frozen_at)}` : ''}`} data-testid={`backup-ok-${r.pack_id}`}>
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                  </div>
                                );
                              }
                              if (hasGridfs) {
                                const isSyncing = syncingId === r.pack_id;
                                return (
                                  <div className="flex justify-center">
                                    <button
                                      type="button"
                                      onClick={() => handleSyncSharePoint(r)}
                                      disabled={isSyncing}
                                      title="Partial backup — saved to GridFS but not yet on SharePoint. Click to sync now."
                                      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-60"
                                      data-testid={`backup-partial-${r.pack_id}`}
                                    >
                                      {isSyncing
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Cloud className="w-3.5 h-3.5" />}
                                      <span className="hidden sm:inline">{isSyncing ? 'Syncing…' : 'Sync'}</span>
                                    </button>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex justify-center" title="No durable backup snapshot yet" data-testid={`backup-none-${r.pack_id}`}>
                                  <span className="text-slate-300 dark:text-slate-600 select-none">—</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => view(r)} data-testid={`view-${r.pack_id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => download(r)} data-testid={`download-${r.pack_id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              {(r.status === 'draft' || r.status === 'sent') && !r.archived && (
                                <LinkActionsMenu
                                  currentLink={r.sign_token ? `${window.location.origin}/sign-contract/${r.sign_token}` : null}
                                  onGenerate={() => generatePackLink(r)}
                                  onResend={() => resend(r)}
                                  busy={linkBusyId === r.pack_id}
                                  testid={r.pack_id}
                                />
                              )}
                              {r.status === 'customer_signed' && (
                                <Button
                                  size="sm"
                                  className="h-8 bg-blue-600 hover:bg-blue-700"
                                  onClick={() => setCounterSign({ customer_id: r.customer_id, pack_id: r.pack_id, customer_name: r.customer_name })}
                                  data-testid={`counter-sign-${r.pack_id}`}
                                >
                                  <FileSignature className="w-3.5 h-3.5 mr-1" /> Counter-sign
                                </Button>
                              )}
                              {r.status === 'customer_signed' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 text-slate-500 hover:text-blue-600"
                                  onClick={() => resendCounterSignNotify(r)}
                                  disabled={resendingNotify === r.pack_id}
                                  title="Re-send the 'awaiting counter-sign' alert email to admins (use if the automatic notification didn't arrive)"
                                  data-testid={`resend-countersign-${r.pack_id}`}
                                >
                                  {resendingNotify === r.pack_id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <BellRing className="w-3.5 h-3.5" />}
                                </Button>
                              )}
                              {r.status !== 'voided' && !r.archived && (
                                <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => { setVoidTarget(r); setVoidReason(''); }} data-testid={`void-${r.pack_id}`} title="Void contract">
                                  <Ban className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {r.archived ? (
                                <Button size="sm" variant="ghost" className="h-8 text-emerald-700" onClick={() => unarchive(r)} data-testid={`unarchive-${r.pack_id}`}>
                                  <ArchiveRestore className="w-3.5 h-3.5" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => archive(r)} data-testid={`archive-${r.pack_id}`}>
                                  <Archive className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {displayedRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-sm text-slate-500" data-testid="no-attention-packs">
                          All contract packs are fully backed up.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Letters of Authorisation — separate signing flow */}
        <Card className="mt-8" data-testid="admin-loa-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="text-lg flex items-center gap-2">
                <FilePen className="w-5 h-5 text-slate-700" />
                Letters of Authorisation
                <Badge variant="outline" className="ml-2 text-xs">{loas.length}</Badge>
              </CardTitle>
              <div className="relative w-72 max-w-full">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={loaSearch}
                  onChange={(e) => setLoaSearch(e.target.value)}
                  placeholder="Search by customer, DNO, status…"
                  className="pl-9 h-9 text-sm"
                  data-testid="loa-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loasLoading ? (
              <div className="py-12 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : filteredLoas.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="empty-loas">
                {loaSearch ? 'No Letters of Authorisation match your search.' : 'No Letters of Authorisation generated yet. Use the "Letter of Authorisation" button on a customer file to create one.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 dark:text-slate-400">
                      <th className="text-left px-3 py-2.5 font-medium">Customer</th>
                      <th className="text-left px-3 py-2.5 font-medium">DNO</th>
                      <th className="text-left px-3 py-2.5 font-medium">App. type</th>
                      <th className="text-left px-3 py-2.5 font-medium">Status</th>
                      <th className="text-left px-3 py-2.5 font-medium">Generated</th>
                      <th className="text-left px-3 py-2.5 font-medium">Signed</th>
                      <th className="text-center px-3 py-2.5 font-medium">Backup</th>
                      <th className="text-right px-3 py-2.5 font-medium w-[1%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLoas.map((l) => {
                      const statusMeta = l.status === 'signed'
                        ? { label: 'Signed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
                        : l.status === 'sent'
                        ? { label: 'Awaiting Customer', cls: 'bg-amber-100 text-amber-700 border-amber-200' }
                        : { label: 'Generated', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                      return (
                        <tr
                          key={l.doc_id}
                          className="border-b border-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                          data-testid={`loa-row-${l.doc_id}`}
                        >
                          <td className="px-3 py-2.5">
                            <Link to={`/customers/${l.customer_id}`} className="text-blue-600 hover:underline font-medium">
                              {l.customer_name}
                            </Link>
                            <div className="text-xs text-slate-500">{l.customer_email}</div>
                          </td>
                          <td className="px-3 py-2.5 text-slate-700 dark:text-slate-200">{l.dno_name || '—'}</td>
                          <td className="px-3 py-2.5">
                            <Badge variant="outline" className="text-xs">{l.application_type || 'G99'}</Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge className={`${statusMeta.cls} border text-[11px]`}>{statusMeta.label}</Badge>
                            {l.reminder_count > 0 && (
                              <Badge variant="outline" className="ml-1 text-[10px]">
                                {l.reminder_count}× reminded
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                            {fmt(l.created_at)}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                            {l.signed_at ? fmt(l.signed_at) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <DocBackupCell
                              synced={l.sharepoint_synced}
                              hasLocal={l.has_local_file}
                              webUrl={l.sharepoint_web_url}
                              syncing={syncingDocId === l.doc_id}
                              onSync={() => handleSyncDoc(l.customer_id, l.doc_id, loadLoas)}
                              testid={l.doc_id}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {l.status !== 'signed' && (
                              <LinkActionsMenu
                                currentLink={l.sign_token ? `${window.location.origin}/sign-variation/${l.sign_token}` : null}
                                onGenerate={() => generateDocLink(l.customer_id, l.doc_id, loadLoas)}
                                onResend={() => sendLoaReminder(l)}
                                busy={linkBusyId === l.doc_id || remindingLoa === l.doc_id}
                                testid={l.doc_id}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Variations — searchable cross-customer list */}
        <Card className="mt-8" data-testid="admin-variations-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-blue-600" />
                Contract Variations
                <Badge variant="outline" className="ml-2 text-xs">{variations.length}</Badge>
              </CardTitle>
              <div className="relative w-72 max-w-full">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={variationSearch}
                  onChange={(e) => setVariationSearch(e.target.value)}
                  placeholder="Search by customer, status, total…"
                  className="pl-9 h-9 text-sm"
                  data-testid="variation-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {variationsLoading ? (
              <div className="py-12 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : filteredVariations.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="empty-variations">
                {variationSearch ? 'No Contract Variations match your search.' : 'No Contract Variations raised yet. Use the "Raise Variation" button on a customer file to create one.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 dark:text-slate-400">
                      <th className="text-left px-3 py-2.5 font-medium">Customer</th>
                      <th className="text-right px-3 py-2.5 font-medium">Original</th>
                      <th className="text-right px-3 py-2.5 font-medium">Net change</th>
                      <th className="text-right px-3 py-2.5 font-medium">New total</th>
                      <th className="text-left px-3 py-2.5 font-medium">Status</th>
                      <th className="text-left px-3 py-2.5 font-medium">Raised</th>
                      <th className="text-left px-3 py-2.5 font-medium">Signed</th>
                      <th className="text-center px-3 py-2.5 font-medium">Backup</th>
                      <th className="text-right px-3 py-2.5 font-medium w-[1%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVariations.map((v) => {
                      const statusMeta = v.status === 'signed'
                        ? { label: 'Signed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
                        : v.status === 'sent'
                        ? { label: 'Awaiting Customer', cls: 'bg-amber-100 text-amber-700 border-amber-200' }
                        : { label: 'Generated', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                      const fmtMoney = (n) => (n == null || n === '' ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                      const netClass = v.value_of_instruction == null
                        ? 'text-slate-500'
                        : Number(v.value_of_instruction) > 0
                        ? 'text-emerald-700'
                        : Number(v.value_of_instruction) < 0
                        ? 'text-rose-700'
                        : 'text-slate-500';
                      return (
                        <tr
                          key={v.doc_id}
                          className="border-b border-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                          data-testid={`variation-row-${v.doc_id}`}
                        >
                          <td className="px-3 py-2.5">
                            <Link to={`/customers/${v.customer_id}`} className="text-blue-600 hover:underline font-medium">
                              {v.customer_name}
                            </Link>
                            <div className="text-xs text-slate-500">{v.customer_email}</div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200 tabular-nums">{fmtMoney(v.original_contract_sum)}</td>
                          <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${netClass}`}>
                            {v.value_of_instruction == null ? '—' : `${Number(v.value_of_instruction) > 0 ? '+' : ''}${fmtMoney(v.value_of_instruction)}`}
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200 font-semibold tabular-nums">{fmtMoney(v.adjusted_total)}</td>
                          <td className="px-3 py-2.5">
                            <Badge className={`${statusMeta.cls} border text-[11px]`}>{statusMeta.label}</Badge>
                            {v.reminder_count > 0 && (
                              <Badge variant="outline" className="ml-1 text-[10px]">
                                {v.reminder_count}× reminded
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">{fmt(v.created_at)}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                            {v.signed_at ? fmt(v.signed_at) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <DocBackupCell
                              synced={v.sharepoint_synced}
                              hasLocal={v.has_local_file}
                              webUrl={v.sharepoint_web_url}
                              syncing={syncingDocId === v.doc_id}
                              onSync={() => handleSyncDoc(v.customer_id, v.doc_id, loadVariations)}
                              testid={v.doc_id}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {v.status !== 'signed' && (
                              <LinkActionsMenu
                                currentLink={v.sign_token ? `${window.location.origin}/sign-variation/${v.sign_token}` : null}
                                onGenerate={() => generateDocLink(v.customer_id, v.doc_id, loadVariations)}
                                onResend={() => sendVariationReminder(v)}
                                busy={linkBusyId === v.doc_id || remindingVariation === v.doc_id}
                                testid={v.doc_id}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Counter-sign dialog */}
        <Dialog open={!!counterSign} onOpenChange={(o) => { if (!o) { setCounterSign(null); setSignature(''); } }}>
          <DialogContent className="sm:max-w-md" data-testid="counter-sign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-blue-600" />
                Counter-sign Contract
              </DialogTitle>
              <DialogDescription>
                {counterSign?.customer_name ? `${counterSign.customer_name} — ` : ''}
                customer has signed. Sign on behalf of Helios to fully execute the contract — a copy will be emailed to the customer automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="text-xs text-slate-600 font-medium block">Nick Lowther — sign below</label>
              <SignaturePad onChange={setSignature} height={150} dataTestId="counter-sign-pad" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setCounterSign(null); setSignature(''); }}>Cancel</Button>
              <Button
                onClick={submitCounterSign}
                disabled={signing || !signature}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="counter-sign-submit"
              >
                {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Submit Signature
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Void contract dialog */}
        <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) { setVoidTarget(null); setVoidReason(''); } }}>
          <DialogContent className="sm:max-w-md" data-testid="void-contract-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Ban className="w-5 h-5" /> Void Contract
              </DialogTitle>
              <DialogDescription>
                Void the contract for <strong>{voidTarget?.customer_name || 'this customer'}</strong>. It stays
                on file marked "Voided", the signing link is disabled, and a fresh contract can be raised
                via the wizard. A reason is required for the audit trail.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-xs text-slate-600 font-medium block">Reason for voiding</label>
              <Textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. Customer changed system spec / pricing error — reissuing contract"
                rows={3}
                data-testid="void-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setVoidTarget(null); setVoidReason(''); }}>Cancel</Button>
              <Button
                onClick={voidPack}
                disabled={voiding || !voidReason.trim()}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="void-contract-submit"
              >
                {voiding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                Void Contract
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminContractPacks;
